// Offline Data Badge Component
// Shows when displaying cached/offline data

import { Database, RefreshCw, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { fr, de, enUS, it } from 'date-fns/locale';

const DATE_LOCALES: Record<string, typeof fr> = { fr, de, en: enUS, it };

interface OfflineDataBadgeProps {
  isFromCache: boolean;
  lastSyncAt: Date | null;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  className?: string;
  compact?: boolean;
}

export function OfflineDataBadge({
  isFromCache,
  lastSyncAt,
  onRefresh,
  isRefreshing = false,
  className = '',
  compact = false,
}: OfflineDataBadgeProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = DATE_LOCALES[i18n.language?.split('-')[0] ?? 'fr'] ?? fr;

  if (!isFromCache) {
    return null;
  }

  const timeAgo = lastSyncAt
    ? formatDistanceToNow(lastSyncAt, { addSuffix: true, locale: dateLocale })
    : t('offline_data_badge.unknown');

  if (compact) {
    return (
      <div
        className={`
          inline-flex items-center gap-1.5 px-2 py-1
          bg-amber-100 text-amber-800 rounded-full text-xs font-medium
          ${className}
        `}
        title={t('offline_data_badge.compact_title', { time: timeAgo })}
      >
        <Database className="w-3.5 h-3.5" />
        <span>{t('offline_data_badge.compact_label')}</span>
      </div>
    );
  }

  return (
    <div
      className={`
        flex items-center gap-3 px-3 py-2
        bg-amber-50 border border-amber-200 rounded-lg
        ${className}
      `}
    >
      <div className="flex items-center gap-2 text-amber-800">
        <Database className="w-4 h-4" />
        <span className="text-sm font-medium">{t('offline_data_badge.title')}</span>
      </div>

      <div className="flex items-center gap-1.5 text-amber-600 text-xs">
        <Clock className="w-3.5 h-3.5" />
        <span>{t('offline_data_badge.sync_time', { time: timeAgo })}</span>
      </div>

      {onRefresh && (
        <button
          onClick={onRefresh}
          disabled={isRefreshing}
          className={`
            ml-auto flex items-center gap-1.5 px-2 py-1
            text-xs font-medium text-amber-700
            bg-amber-100 hover:bg-amber-200 rounded
            transition-colors duration-200
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
        >
          <RefreshCw
            className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`}
          />
          {isRefreshing ? t('offline_data_badge.syncing') : t('offline_data_badge.refresh')}
        </button>
      )}
    </div>
  );
}

// Inline version for headers
export function OfflineDataIndicator({
  isFromCache,
  className = '',
}: {
  isFromCache: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  if (!isFromCache) {
    return null;
  }

  return (
    <span
      className={`
        inline-flex items-center gap-1 px-1.5 py-0.5
        bg-amber-100 text-amber-700 rounded text-xs
        ${className}
      `}
      title={t('offline_data_badge.indicator_title')}
    >
      <Database className="w-3 h-3" />
    </span>
  );
}

export default OfflineDataBadge;
